import { useStudioStore } from '../../stores/studioStore';
import { formatBarsBeats, clsx } from '../../lib/utils';

export function TransportBar() {
  const transport = useStudioStore((s) => s.transport);
  const project = useStudioStore((s) => s.project);
  const session = useStudioStore((s) => s.session);
  const togglePlay = useStudioStore((s) => s.togglePlay);
  const stop = useStudioStore((s) => s.stop);
  const toggleRecord = useStudioStore((s) => s.toggleRecord);
  const setBpm = useStudioStore((s) => s.setBpm);
  const setView = useStudioStore((s) => s.setView);
  const setSessionQuantize = useStudioStore((s) => s.setSessionQuantize);
  const setStopPolicy = useStudioStore((s) => s.setStopPolicy);
  const setLaunchMode = useStudioStore((s) => s.setLaunchMode);
  const toggleSessionCapture = useStudioStore((s) => s.toggleSessionCapture);
  const stopAllSessionClips = useStudioStore((s) => s.stopAllSessionClips);
  const addTrack = useStudioStore((s) => s.addTrack);

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-daw-border bg-daw-surface px-3 py-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={clsx(
            'h-8 w-8 rounded border border-daw-border bg-daw-surface2 text-sm',
            transport.recording && 'border-red-500 bg-red-600 text-white',
          )}
          title="Record"
          onClick={toggleRecord}
        >
          ●
        </button>
        <button
          type="button"
          className="h-8 w-8 rounded border border-daw-border bg-daw-surface2 text-sm"
          title="Stop"
          onClick={stop}
        >
          ■
        </button>
        <button
          type="button"
          className={clsx(
            'h-8 w-8 rounded border border-daw-border bg-daw-surface2 text-sm',
            transport.playing && 'border-emerald-500 bg-emerald-600 text-white',
          )}
          title="Play / Pause"
          onClick={togglePlay}
        >
          ▶
        </button>
      </div>

      <div className="rounded border border-daw-border bg-black/40 px-2 py-1 font-mono text-xs text-daw-accent">
        {formatBarsBeats(transport.playheadBeat, transport.timeSig[0])}
      </div>

      <label className="flex items-center gap-1 text-xs text-daw-muted">
        BPM
        <input
          type="number"
          min={40}
          max={300}
          value={transport.bpm}
          onChange={(e) => setBpm(Number(e.target.value) || 120)}
          className="w-16 rounded border border-daw-border bg-daw-bg px-1 py-1 text-daw-text"
        />
      </label>

      <div className="mx-1 h-6 w-px bg-daw-border" />

      {(['session', 'arrange', 'mix', 'both'] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => setView(v)}
          className={clsx(
            'rounded border px-2 py-1 text-xs capitalize',
            project.view === v
              ? 'border-daw-accent2 bg-daw-accent2/20 text-white'
              : 'border-daw-border bg-daw-surface2 text-daw-muted',
          )}
        >
          {v}
        </button>
      ))}

      <select
        className="rounded border border-daw-border bg-daw-bg px-2 py-1 text-xs"
        value={session.quantize}
        onChange={(e) => setSessionQuantize(e.target.value as typeof session.quantize)}
        title="Launch quantize"
      >
        <option value="bar">Q: 1 Bar</option>
        <option value="beat">Q: 1 Beat</option>
        <option value="1/8">Q: 1/8</option>
        <option value="1/16">Q: 1/16</option>
        <option value="free">Q: None</option>
      </select>

      <select
        className="rounded border border-daw-border bg-daw-bg px-2 py-1 text-xs"
        value={session.stopPolicy}
        onChange={(e) => setStopPolicy(e.target.value as typeof session.stopPolicy)}
      >
        <option value="exclusive">Stop: Exclusive</option>
        <option value="legato">Stop: Legato</option>
      </select>

      <select
        className="rounded border border-daw-border bg-daw-bg px-2 py-1 text-xs"
        value={session.defaultLaunchMode}
        onChange={(e) => setLaunchMode(e.target.value as typeof session.defaultLaunchMode)}
      >
        <option value="trigger">Mode: Trigger</option>
        <option value="gate">Mode: Gate</option>
        <option value="toggle">Mode: Toggle</option>
      </select>

      <button
        type="button"
        className="rounded border border-daw-border bg-daw-surface2 px-2 py-1 text-xs"
        onClick={stopAllSessionClips}
      >
        ■ Stop Clips
      </button>

      <button
        type="button"
        className={clsx(
          'rounded border px-2 py-1 text-xs',
          session.capture
            ? 'border-red-500 bg-red-600 text-white'
            : 'border-daw-border bg-daw-surface2',
        )}
        onClick={toggleSessionCapture}
      >
        ● Sess→Arr
      </button>

      <div className="flex-1" />

      <button
        type="button"
        className="rounded border border-daw-border bg-daw-surface2 px-2 py-1 text-xs"
        onClick={() => addTrack()}
      >
        + Track
      </button>
      <button
        type="button"
        className="rounded border border-daw-border bg-daw-surface2 px-2 py-1 text-xs"
        onClick={() => addTrack({ type: 'midi', name: 'MIDI' })}
      >
        + MIDI
      </button>

      <span className="text-xs font-semibold text-daw-text">{project.name}</span>
      <span className="rounded bg-daw-accent2/20 px-1.5 py-0.5 text-[10px] font-bold text-daw-accent2">
        React Shell
      </span>
    </div>
  );
}
